import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { skip, takeUntil, tap } from 'rxjs/operators';

import {
  DisposalSite,
  DisposalSiteObject,
} from '@digital-platform/disposal-sites/domain';
import { Entity } from '@digital-platform/shared/domain';
import { ErrorService } from '@digital-platform/shared/services';
import { LoginSessionFacadeService } from '@digital-platform/users/services';
import { DisposalSitesService } from '../disposal-sites/disposal-sites.service';

@Injectable({
  providedIn: 'root',
})
export class DisposalSitesFacadeService implements OnDestroy {
  private destroyed$ = new Subject<void>();

  private loading = new BehaviorSubject<boolean>(false);
  public loading$ = this.loading.asObservable();

  private disposalSites = new BehaviorSubject<DisposalSite[]>([]);
  private _disposalSites$ = this.disposalSites.asObservable();
  public get disposalSites$(): Observable<DisposalSite[]> {
    if (this.disposalSites.value.length === 0 && !this.loading.value) {
      this.load();
    }

    if (this.loading.value) {
      // skips the existing value since we're loading a new one
      return this._disposalSites$.pipe(skip(1));
    }

    return this._disposalSites$;
  }

  private deepWellSites = new BehaviorSubject<DisposalSite[]>([]);
  private _deepWellSites$ = this.deepWellSites.asObservable();
  public get deepWellSites$(): Observable<DisposalSite[]> {
    if (this.deepWellSites.value.length === 0 && !this.loading.value) {
      this.loadDeepWell();
    }

    if (this.loading.value) {
      // skips the existing value since we're loading a new one
      return this._deepWellSites$.pipe(skip(1));
    }

    return this._deepWellSites$;
  }

  private disposalSiteObjInitVals: DisposalSiteObject = {
    document_type: 'DISPOSALSITE',
    active: true,
    type: 'landfill',
    id: '',
    name: '',
    class: [],
    address: {
      id: '',
      addressHash: '',
      addressLine1: '',
      addressLine2: '',
      addressLine3: '',
      city: '',
      countryCode: 'US',
      country: 'United States',
      county: '',
      display: '',
      isCloseMatch: '',
      latitude: '',
      locationCode: '',
      locationConfidenceScore: '',
      locationType: '',
      longitude: '',
      matchConfidenceScore: '',
      matchType: '',
      postalCode: '',
      postalCodeSuffix: '',
      stateCode: '',
      timeZoneCode: '',
      timeZoneName: '',
    },
    divisionNumber: '',
    isHazardousAccepted: false,
    isRailcarAccepted: false,
    isVocConcentrationRequired: false,
    isDryWeightRequired: false,
    isFriableAsbestosAccepted: true,
    isNonFriableAsbestosAccepted: true,
  };
  private newDisposalSite = new BehaviorSubject<DisposalSiteObject>(
    this.disposalSiteObjInitVals
  );
  public _newDisposalSite$ = this.newDisposalSite.asObservable();

  constructor(
    private service: DisposalSitesService,
    private readonly errorService: ErrorService,
    private readonly loginSessionService: LoginSessionFacadeService
  ) {
    this.loginSessionService.loginSession$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((session) => {
        if (session) {
          this.refresh();
        } else {
          this.clear();
        }
      });
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public clear(): void {
    this.disposalSites.next([]);
  }

  public refresh(): void {
    this.clear();
    this.load();
  }

  private load() {
    this.errorService.clearErrors();
    this.loading.next(true);

    this.service.getDisposalSites().subscribe({
      next: (disposalSites) =>
        this.disposalSites.next(
          this.updateCollection(this.disposalSites.value, disposalSites)
        ),
      error: (error) => {
        this.errorService.raiseError(error);
        this.loading.next(false);
      },
      complete: () => this.loading.next(false),
    });
  }

  private loadDeepWell() {
    this.errorService.clearErrors();
    this.loading.next(true);

    this.service.getDeepWellSites().subscribe({
      next: (disposalSites) =>
        this.deepWellSites.next(
          this.updateCollection(this.deepWellSites.value, disposalSites)
        ),
      error: (error) => {
        this.errorService.raiseError(error);
        this.loading.next(false);
      },
      complete: () => this.loading.next(false),
    });
  }

  private updateCollection<T extends Entity>(
    collection: T[],
    updates: T | T[]
  ): T[] {
    let updateArray: T[] = [];
    if (!Array.isArray(updates)) {
      updateArray = [updates];
    } else {
      updateArray = updates;
    }

    let updatedCollection: T[] = [...collection];

    updateArray.forEach((item) => {
      const found = updatedCollection.findIndex((i) => i.id === item.id);

      if (found !== -1) {
        // we found it in the local collection, update it
        updatedCollection = [
          ...updatedCollection.slice(0, found),
          item,
          ...updatedCollection.slice(found + 1),
        ];
      } else {
        // it's not found in the local collection, add it
        updatedCollection = [...updatedCollection, item];
      }
    });

    return updatedCollection;
  }

  public addDisposalSite(
    disposalSite: DisposalSiteObject
  ): Observable<DisposalSiteObject> {
    return this.service
      .createDisposalSite(disposalSite)
      .pipe(
        tap((newDisposalSite) => this.newDisposalSite.next(newDisposalSite))
      );
  }
}
